/**
 * WelcomeBack Component
 *
 * A clean, minimal welcome message that appears when users log in.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import './WelcomeBack.css';

const WelcomeBack = ({ isVisible, onClose, userName, greeting }) => {
  const [isClosing, setIsClosing] = useState(false);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose?.();
      setIsClosing(false);
    }, 250);
  }, [onClose]);

  // Auto-close after 6 seconds
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(handleClose, 6000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, handleClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVisible) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isVisible, handleClose]);

  if (!isVisible) return null;

  // Get time-based greeting if not provided
  const getTimeGreeting = () => {
    if (greeting) return greeting;
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get first name
  const firstName = userName?.split(' ')[0] || userName || 'there';

  const content = (
    <div
      className={`wb-overlay ${isClosing ? 'wb-closing' : ''}`}
      onClick={handleClose}
    >
      {/* Main Content */}
      <div
        className="wb-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button className="wb-close-btn" onClick={handleClose}>
          <FiX />
        </button>

        {/* Welcome Text */}
        <div className="wb-text-container">
          <span className="wb-greeting">{getTimeGreeting()},</span>
          <h1 className="wb-name">{firstName}</h1>
          <div className="wb-divider" />
          <p className="wb-message">Welcome back to Clockwise</p>
        </div>

        {/* Progress indicator for auto-close */}
        <div className="wb-progress-bar">
          <div className="wb-progress-fill" />
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default WelcomeBack;
